/* eslint-disable */

import React, { useState, useEffect } from 'react'
import { Box } from '@mui/material'

interface SkeinIconProps {
  color: string | string[]
  size?: number
}

// Helper function to determine if color is light or dark
const isLightColor = (hex: string): boolean => {
  const cleanHex = hex.replace('#', '').toUpperCase()
  
  // Handle black and white specifically
  if (cleanHex === '000000' || cleanHex === '000' || cleanHex === 'FFF' || cleanHex === 'FFFFFF') {
    return cleanHex === 'FFF' || cleanHex === 'FFFFFF'
  }
  
  if (cleanHex.length === 3) {
    // Handle 3-digit hex
    const r = parseInt(cleanHex[0] + cleanHex[0], 16)
    const g = parseInt(cleanHex[1] + cleanHex[1], 16)
    const b = parseInt(cleanHex[2] + cleanHex[2], 16)
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255
    return luminance > 0.5
  }
  const r = parseInt(cleanHex.substring(0, 2), 16)
  const g = parseInt(cleanHex.substring(2, 4), 16)
  const b = parseInt(cleanHex.substring(4, 6), 16)
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255
  return luminance > 0.5
}

export default function SkeinIcon({ color, size = 32 }: SkeinIconProps) {
  const [svgContent, setSvgContent] = useState<string>('')
  const colors = Array.isArray(color) ? color : [color]
  const primaryColor = colors[0] || '#000000'
  const strokeColor = isLightColor(primaryColor) ? '#000' : '#fff'

  useEffect(() => {
    fetch('/skein.svg')
      .then(res => res.text())
      .then(text => {
        const textColor = isLightColor(primaryColor) ? '#000' : '#fff'

        if (colors.length > 1) {
          // Multi-color: use linear gradient for stripes
          const gradientId = `skein-gradient-${colors.join('-').replace(/[^a-z0-9]/gi, '')}`
          const stripeStops = colors.flatMap((c, i) => [
            `<stop offset="${(i / colors.length) * 100}%" stop-color="${c}"/>`,
            `<stop offset="${((i + 1) / colors.length) * 100}%" stop-color="${c}"/>`
          ]).join('')
          const gradientDef = `<defs><linearGradient id="${gradientId}" x1="0%" y1="0%" x2="100%" y2="0%">${stripeStops}</linearGradient></defs>`

          let modifiedSvg = text
            .replace(/<svg([^>]*)>/, `<svg$1>${gradientDef}`)
            .replace(/fill="#000000"/g, `fill="url(#${gradientId})"`)
            .replace(/<path d=/g, `<path stroke="${strokeColor}" stroke-width="2" d=`)
            .replace(/<svg([^>]*)>/, `<svg$1 style="transform: rotate(90deg);">`)
            .replace(/(<\/g>\s*<\/g>\s*<\/svg>)/, `</g></g></svg>`)
          setSvgContent(modifiedSvg)
        } else {
          const normalizedColor = primaryColor.toUpperCase()
          let modifiedSvg = text
            .replace(/fill="#000000"/g, `fill="${primaryColor}"`)
            .replace(/<path d=/g, `<path stroke="${strokeColor}" stroke-width="2" d=`)
            .replace(/<svg([^>]*)>/, `<svg$1 style="transform: rotate(90deg);">`)
            .replace(/(<\/g>\s*<\/g>\s*<\/svg>)/, `<text x="148" y="160" text-anchor="middle" fill="${textColor}" font-size="24" font-weight="bold" font-family="Arial, sans-serif">${normalizedColor}</text>$1`)
          setSvgContent(modifiedSvg)
        }
      })
      .catch(err => {
        console.error('Error loading SVG:', err)
      })
  }, [colors.join(','), primaryColor, strokeColor])

  if (!svgContent) {
    return null
  }

  const aspectRatio = 296.054 / 296.053 // height / width (viewBox="0 0 296.053 296.054")

  return (
    <Box
      component="div"
      sx={{
        display: 'inline-block',
        verticalAlign: 'middle',
        width: size,
        height: size * aspectRatio,
        '& svg': {
          width: '100%',
          height: '100%'
        }
      }}
      dangerouslySetInnerHTML={{ __html: svgContent }}
    />
  )
}
